import React from 'react';
import { useNavigate } from 'react-router-dom';
import SharedNav from '@/components/SharedNav';
import {
  Sparkles,
  ShieldCheck,
  Eye,
  CheckCircle2,
  History,
  Droplets,
  Bug,
  Calculator,
  ArrowRight,
  BookOpen,
  AlertTriangle,
  LineChart as LineChartIcon,
} from 'lucide-react';
import ConfidenceChip from '@/components/predict/ConfidenceChip';
import DataSourceLine from '@/components/predict/DataSourceLine';
import MethodologyLink from '@/components/predict/MethodologyLink';
import UncertaintyBand from '@/components/predict/UncertaintyBand';
import ValidationStatus from '@/components/predict/ValidationStatus';
import DisclaimerBanner from '@/components/predict/DisclaimerBanner';

const PHASE1_MODULES = [
  {
    key: 'historical-yield',
    icon: History,
    label: 'Historical Yield Explorer',
    blurb:
      'Yield, rainfall and temperature for your district & crop, sourced from IMD and DGCIS. Visualises the past — does not predict the future.',
    confidence: 'No prediction · 100 % archival',
    sources: 'IMD · AGMARKNET · DGCIS',
    sprint: 'Sprint 1 · ~2 weeks',
  },
  {
    key: 'water-calculator',
    icon: Droplets,
    label: 'Water Requirement Calculator',
    blurb:
      'FAO-56 dual-Kc reference ETo + crop water demand per phenological stage. Validated against field lysimeter data (±10–15 % typical band).',
    confidence: 'High · FAO-56 closed-form',
    sources: 'NASA POWER · IMD AWS · SoilGrids 2.0',
    sprint: 'Sprint 2 · ~3 weeks',
  },
  {
    key: 'pest-tracker',
    icon: Bug,
    label: 'Degree-Day Pest Tracker',
    blurb:
      'GDD accumulator (single-sine, Baskerville–Emin 1969) tracking pest developmental stages against published ICAR-NCIPM thresholds.',
    confidence: 'Moderate · advisory not prescription',
    sources: 'IMD · ICAR-NCIPM · TNAU bulletins',
    sprint: 'Sprint 3 · ~3 weeks',
  },
  {
    key: 'budget',
    icon: Calculator,
    label: 'Crop Budget Calculator',
    blurb:
      'Cost of cultivation, gross return, B:C ratio, break-even yield. Defaults from CACP cost reports — every assumption visible and editable.',
    confidence: 'High · deterministic arithmetic',
    sources: 'CACP cost-of-cultivation · AGMARKNET',
    sprint: 'Sprint 4 · ~2 weeks',
  },
];

const TRUST_PROMISES = [
  {
    icon: CheckCircle2,
    title: 'Validated models',
    body:
      'Only peer-reviewed methods (FAO-56, DSSAT, APSIM, InfoCrop, GDD). No black-box AI marketed as a prediction. Every equation is documented on a public methodology page.',
  },
  {
    icon: Eye,
    title: 'Visible uncertainty',
    body:
      'Every numeric output carries a confidence chip, an uncertainty band (±, IQR, or stage), the data source, and the date the model was last cross-checked against ICAR / SAU field data.',
  },
  {
    icon: ShieldCheck,
    title: 'Verified accuracy',
    body:
      'A public Validation Dashboard publishes our on-the-ground accuracy per module per crop per state. We will refuse to ship a number when the science cannot support it.',
  },
];

export default function Predict() {
  const navigate = useNavigate();
  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SharedNav />

      <main className="max-w-[1180px] mx-auto px-6 pt-12 pb-24" data-testid="predict-page">
        {/* Hero */}
        <section className="mb-14" data-testid="predict-hero">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5"
            style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 600 }}
            data-testid="predict-coming-soon-badge"
          >
            <Sparkles size={13} /> Coming soon · Strategic foundation in review
          </div>

          <h1
            className="font-display"
            style={{
              fontSize: 48,
              lineHeight: 1.08,
              letterSpacing: '-0.015em',
              color: 'var(--green-900)',
              maxWidth: 880,
              fontWeight: 400,
            }}
            data-testid="predict-headline"
          >
            Honest predictions for Indian agriculture.
          </h1>
          <p
            className="font-display"
            style={{
              fontSize: 22,
              lineHeight: 1.4,
              color: '#3F4A3D',
              marginTop: 14,
              maxWidth: 760,
              fontWeight: 400,
              fontStyle: 'italic',
            }}
            data-testid="predict-subhead"
          >
            Validated models &nbsp;·&nbsp; Visible uncertainty &nbsp;·&nbsp; Verified accuracy.
          </p>

          <p
            className="font-body"
            style={{ fontSize: 15, lineHeight: 1.7, color: '#4F4B44', marginTop: 22, maxWidth: 760 }}
          >
            Most agritech apps quote single-point yield forecasts and 30-day price predictions
            with the confidence of a fortune cookie. We refuse to. The Predict module pairs
            peer-reviewed agronomic science (FAO-56, DSSAT, APSIM, InfoCrop, degree-day phenology)
            with archived public data from IMD and AGMARKNET, and surfaces every output with the
            uncertainty band, the data source, and the date it was last validated against ICAR or
            State Agricultural University field measurements. Where the science cannot support a
            number, we say so — and we show the literature instead.
          </p>
        </section>

        {/* Trust promises */}
        <section className="mb-16" data-testid="predict-trust">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TRUST_PROMISES.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="rounded-2xl p-6"
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #E5E7DD',
                    boxShadow: '0 1px 2px rgba(20,30,15,0.04)',
                  }}
                  data-testid={`predict-promise-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    className="inline-flex items-center justify-center mb-4"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: '#EAF2E5',
                      color: '#2D6A4F',
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display" style={{ fontSize: 19, color: '#1B4332', marginBottom: 8 }}>
                    {p.title}
                  </h3>
                  <p className="font-body" style={{ fontSize: 13.5, lineHeight: 1.65, color: '#4F4B44' }}>
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Phase 1 modules — preview cards */}
        <section className="mb-16" data-testid="predict-phase1">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-display" style={{ fontSize: 28, color: '#1B4332', fontWeight: 400 }}>
              Phase 1 modules
            </h2>
            <span
              className="font-body"
              style={{ fontSize: 12, color: '#8A8580', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              Locked build order
            </span>
          </div>
          <p
            className="font-body"
            style={{ fontSize: 14, color: '#6F6A63', marginBottom: 22, maxWidth: 720 }}
          >
            Lowest-risk modules ship first. Each module is exposed in production only after a
            successful ICAR / SAU validation pilot. Each module gets its own methodology page.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PHASE1_MODULES.map((m, idx) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.key}
                  className="rounded-2xl p-6 transition-shadow"
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #E5E7DD',
                    boxShadow: '0 1px 3px rgba(20,30,15,0.04)',
                  }}
                  data-testid={`predict-module-${m.key}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 10,
                        background: '#EAF2E5',
                        color: '#2D6A4F',
                      }}
                    >
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="font-body"
                          style={{
                            fontSize: 11,
                            color: '#2D6A4F',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                          }}
                        >
                          {`Phase 1.${idx + 1}`}
                        </span>
                        <span
                          className="font-body"
                          style={{ fontSize: 11, color: '#8A8580' }}
                        >
                          · {m.sprint}
                        </span>
                      </div>
                      <h3 className="font-display" style={{ fontSize: 20, color: '#1B4332', marginBottom: 6 }}>
                        {m.label}
                      </h3>
                      <p
                        className="font-body"
                        style={{ fontSize: 13.5, lineHeight: 1.65, color: '#4F4B44', marginBottom: 14 }}
                      >
                        {m.blurb}
                      </p>
                      <div
                        style={{
                          borderTop: '1px dashed #E5E7DD',
                          paddingTop: 12,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 8,
                        }}
                      >
                        <div>
                          <div
                            className="font-body"
                            style={{ fontSize: 10.5, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}
                          >
                            Confidence
                          </div>
                          <div className="font-body" style={{ fontSize: 12.5, color: '#3F4A3D', fontWeight: 500 }}>
                            {m.confidence}
                          </div>
                        </div>
                        <div>
                          <div
                            className="font-body"
                            style={{ fontSize: 10.5, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}
                          >
                            Sources
                          </div>
                          <div className="font-body" style={{ fontSize: 12.5, color: '#3F4A3D', fontWeight: 500 }}>
                            {m.sources}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <MethodologyLink module={m.key} />
                        <ValidationStatus status="pilot-pending" />
                        {m.key === 'historical-yield' && (
                          <button
                            type="button"
                            onClick={() => navigate('/predict/historical-yield')}
                            className="inline-flex items-center gap-1 transition-colors"
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              color: '#FFFFFF',
                              background: '#2D6A4F',
                              border: '1px solid #1B4332',
                              padding: '4px 10px',
                              borderRadius: 6,
                              cursor: 'pointer',
                            }}
                            data-testid={`open-module-${m.key}`}
                          >
                            Open module
                            <ArrowRight size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Trust Infrastructure — Sprint 0 live components */}
        <section className="mb-16" data-testid="predict-trust-preview">
          <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
            <h2 className="font-display" style={{ fontSize: 28, color: '#1B4332', fontWeight: 400 }}>
              Trust Infrastructure — Sprint 0 live
            </h2>
            <span
              className="inline-flex items-center gap-1.5"
              style={{
                background: '#E6F4EA',
                color: '#1B5E20',
                border: '1px solid #A5D6A7',
                borderRadius: 999,
                padding: '4px 11px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              <CheckCircle2 size={12} /> Shipped
            </span>
          </div>
          <p
            className="font-body"
            style={{ fontSize: 14, color: '#6F6A63', marginBottom: 22, maxWidth: 760 }}
          >
            The trust components ship before the modules. Every numeric output in the Predict
            tab will compose these primitives. Hover them — they're interactive.
          </p>

          {/* Live disclaimer banner sample */}
          <DisclaimerBanner module="predict-landing-preview" tone="info" />

          {/* Component gallery — 2-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ConfidenceChip showcase */}
            <PreviewCard
              title="ConfidenceChip"
              testid="preview-confidence-chip"
              blurb="Colour-coded model confidence. Hover for the one-line reason."
            >
              <div className="flex flex-wrap items-center gap-2">
                <ConfidenceChip level="high" reason="Closed-form FAO-56, validated ±10–15 % vs lysimeter." />
                <ConfidenceChip level="moderate" reason="GDD model — accurate for development, not population size." />
                <ConfidenceChip level="low" reason="Only 12 years of district data; uncertainty is wide." />
                <ConfidenceChip level="insufficient" reason="No calibrated cultivar file for your zone yet." />
              </div>
            </PreviewCard>

            {/* UncertaintyBand showcase */}
            <PreviewCard
              title="UncertaintyBand"
              testid="preview-uncertainty-band"
              blurb="Every numeric output ships with its uncertainty representation. The band IS the honesty."
            >
              <div className="flex flex-col gap-3">
                <UncertaintyBand value={245} unit="mm" kind="plus-minus" errorPct={12} size="md" />
                <UncertaintyBand value={1.8} unit="t/ha" kind="iqr" lower={1.4} upper={2.3} size="md" />
                <UncertaintyBand value={342} unit="GDD" kind="range" lower={320} upper={360} size="md" />
                <UncertaintyBand value="Drought-likely" kind="class" size="md" />
              </div>
            </PreviewCard>

            {/* DataSourceLine showcase */}
            <PreviewCard
              title="DataSourceLine"
              testid="preview-data-source-line"
              blurb="Citation + last-updated under every chart. Click-through to provider URL."
            >
              <div className="space-y-3">
                <DataSourceLine
                  sources={[
                    { name: 'IMD AWS Jodhpur', url: 'https://api.imd.gov.in/' },
                    { name: 'NASA POWER', url: 'https://power.larc.nasa.gov/' },
                  ]}
                  lastUpdated="2026-02-08"
                />
                <DataSourceLine
                  sources={['AGMARKNET 2.0 (data.gov.in)']}
                  lastUpdated="2026-02-15"
                />
              </div>
            </PreviewCard>

            {/* MethodologyLink showcase */}
            <PreviewCard
              title="MethodologyLink"
              testid="preview-methodology-link"
              blurb="One-click access to the per-module methodology page. Opens in a new tab so user's work is preserved."
            >
              <div className="flex flex-wrap gap-2">
                <MethodologyLink module="historical-yield" />
                <MethodologyLink module="water-calculator" />
                <MethodologyLink module="pest-tracker" />
                <MethodologyLink module="budget" />
              </div>
            </PreviewCard>

            {/* ValidationStatus showcase */}
            <PreviewCard
              title="ValidationStatus"
              testid="preview-validation-status"
              blurb="Per-module pilot status with last-validated date. Demoted to BETA if validation older than 18 months."
            >
              <div className="flex flex-col gap-2 items-start">
                <ValidationStatus status="validated" lastValidated="2026-01-12" pilot="ICAR-CAZRI Jodhpur" />
                <ValidationStatus status="beta" lastValidated="2024-08-30" pilot="AU Jodhpur" />
                <ValidationStatus status="pilot-pending" />
                <ValidationStatus status="no-data" />
              </div>
            </PreviewCard>

            {/* DisclaimerBanner showcase */}
            <PreviewCard
              title="DisclaimerBanner"
              testid="preview-disclaimer-banner"
              blurb="Persistent at the top of every Predict module. Dismissible per session per module via sessionStorage."
            >
              <DisclaimerBanner
                module="preview-warning-tone"
                tone="warning"
                message="This is a Tier-B module — the calibration was last refreshed in 2024. Treat outputs as advisory."
              />
            </PreviewCard>
          </div>

          {/* Action strip — open methodology + validation pages */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/predict/methodology/water-calculator')}
              className="inline-flex items-center gap-2 transition-colors btn-press"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#FFFFFF',
                background: '#2D6A4F',
                border: '1px solid #1B4332',
                padding: '9px 16px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              data-testid="open-methodology-sample"
            >
              <BookOpen size={14} />
              View sample methodology page (Water Calculator)
              <ArrowRight size={14} />
            </button>
            <button
              onClick={() => navigate('/predict/validation')}
              className="inline-flex items-center gap-2 transition-colors btn-press"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#1B4332',
                background: '#FFFFFF',
                border: '1px solid #2D6A4F',
                padding: '9px 16px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              data-testid="open-validation-dashboard"
            >
              <LineChartIcon size={14} />
              Open Validation Dashboard
              <ArrowRight size={14} />
            </button>
          </div>
        </section>

        {/* Crops in scope */}
        <section className="mb-16" data-testid="predict-scope">
          <h2 className="font-display" style={{ fontSize: 24, color: '#1B4332', fontWeight: 400, marginBottom: 8 }}>
            Crops in initial scope
          </h2>
          <p
            className="font-body"
            style={{ fontSize: 14, color: '#6F6A63', marginBottom: 18, maxWidth: 720 }}
          >
            Aligned with the platform's existing biometrical pipelines and AU Jodhpur
            arid-zone research priorities. Additional crops will follow validation pilots.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              'Pearl millet (Bajra)',
              'Moth bean',
              'Cluster bean (Guar)',
              'Chickpea',
              'Finger millet',
              'Sesame',
              'Rice',
              'Wheat',
              'Mustard',
            ].map((crop) => (
              <span
                key={crop}
                className="font-body"
                style={{
                  fontSize: 13,
                  padding: '7px 13px',
                  borderRadius: 999,
                  background: '#F5F2EC',
                  color: '#3F4A3D',
                  border: '1px solid #E5E7DD',
                  fontWeight: 500,
                }}
                data-testid={`predict-crop-chip-${crop.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                {crop}
              </span>
            ))}
          </div>
        </section>

        {/* Honest non-features (the refusals) */}
        <section className="mb-16" data-testid="predict-refusals">
          <div
            className="rounded-2xl p-7"
            style={{
              background: '#FFFBF1',
              border: '1px solid #F5E5B8',
            }}
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={20} style={{ color: '#92400E', marginTop: 2 }} />
              <div>
                <h3 className="font-display" style={{ fontSize: 22, color: '#1B4332', marginBottom: 4, fontWeight: 400 }}>
                  What this module will <em style={{ fontStyle: 'italic' }}>not</em> do
                </h3>
                <p className="font-body" style={{ fontSize: 13.5, color: '#6B5500', lineHeight: 1.6 }}>
                  Honesty is a feature, not a footnote. We refuse to ship the following because
                  the underlying science cannot support them at the per-farmer level today.
                </p>
              </div>
            </div>

            <ul className="font-body" style={{ fontSize: 13.5, lineHeight: 1.7, color: '#3F4A3D', listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ paddingLeft: 18, position: 'relative', marginBottom: 8 }}>
                <span style={{ position: 'absolute', left: 0, color: '#92400E', fontWeight: 700 }}>×</span>
                Single-point market-price forecasts at a 30-day horizon (we show 5-year median + IQR seasonality instead).
              </li>
              <li style={{ paddingLeft: 18, position: 'relative', marginBottom: 8 }}>
                <span style={{ position: 'absolute', left: 0, color: '#92400E', fontWeight: 700 }}>×</span>
                Exact-yield estimates for a specific farm without uncertainty intervals.
              </li>
              <li style={{ paddingLeft: 18, position: 'relative', marginBottom: 8 }}>
                <span style={{ position: 'absolute', left: 0, color: '#92400E', fontWeight: 700 }}>×</span>
                Climate-change projections to 2050 generated on-the-fly per user (we link to peer-reviewed projections).
              </li>
              <li style={{ paddingLeft: 18, position: 'relative', marginBottom: 8 }}>
                <span style={{ position: 'absolute', left: 0, color: '#92400E', fontWeight: 700 }}>×</span>
                Soil quality inferred from a phone photo or NDVI alone.
              </li>
              <li style={{ paddingLeft: 18, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#92400E', fontWeight: 700 }}>×</span>
                Any output framed as "AI prediction" without showing the model, the training data, and the validated error.
              </li>
            </ul>
          </div>
        </section>

        {/* Roadmap CTA */}
        <section data-testid="predict-roadmap-strip">
          <div
            className="rounded-2xl p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
            style={{
              background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)',
              color: '#FFFFFF',
            }}
          >
            <div>
              <h3 className="font-display" style={{ fontSize: 22, fontWeight: 400, color: '#FFFFFF', marginBottom: 6 }}>
                Sprint 0 shipped · Sprint 1 awaiting API keys
              </h3>
              <p className="font-body" style={{ fontSize: 13.5, color: '#D8E5DC', maxWidth: 640 }}>
                Trust Infrastructure components are live above. Methodology and Validation page shells are wired.
                The full 6-part feasibility assessment is at
                <code style={{ background: 'rgba(255,255,255,0.10)', padding: '2px 6px', borderRadius: 4, marginLeft: 6, marginRight: 6, fontSize: 12 }}>
                  /app/memory/PREDICTIVE_FEASIBILITY.md
                </code>
                · Sprint 1 (Historical Yield Explorer) starts once IMD + data.gov.in API keys are provisioned.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <BookOpen size={18} style={{ color: '#A8E0BF' }} />
              <span className="font-body" style={{ fontSize: 13, color: '#A8E0BF' }}>Sprint 0 &nbsp;→&nbsp; Sprint 1 &nbsp;</span>
              <ArrowRight size={16} style={{ color: '#A8E0BF' }} />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function PreviewCard({ title, blurb, testid, children }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <h3 className="font-display" style={{ fontSize: 16, color: '#1B4332', fontWeight: 500 }}>
          &lt;{title} /&gt;
        </h3>
        <span
          className="font-body"
          style={{ fontSize: 10.5, color: '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}
        >
          shipped
        </span>
      </div>
      <p className="font-body" style={{ fontSize: 12.5, color: '#6F6A63', lineHeight: 1.55, marginBottom: 14 }}>
        {blurb}
      </p>
      <div
        className="rounded-lg p-4"
        style={{ background: '#FAFBF9', border: '1px dashed #D5DDC9' }}
      >
        {children}
      </div>
    </div>
  );
}
